import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { guardArray, isInArray } from "@msuite/utils/array";
import { localized } from "@msuite/utils/locales";
import { generateId } from "@msuite/utils/strings";
import clsx from "clsx";
import React from "react";
import {
	type Control,
	Controller,
	type FieldValues,
	type Path,
	type PathValue,
} from "react-hook-form";
import { Divider } from "../divider";
import { Icon } from "../icons";
import { useTranslation } from "../localization";
import { Portal } from "../portal";
import type { ClassNameProps } from "../types";
import { cn } from "../utils";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";
import { RemoveWrapper } from "./remove-wrapper";
import { Overlay } from "../overlay";

/** Props Interface */
export interface SelectOption<U, V> {
	value: V;
	label: string;
	item: U | null;
	cyId?: string;
	/** Renders the option greyed and unselectable — for choices the current state already contains. */
	isDisabled?: boolean;
}

export type SelectRenderOption<U, V> = (item: U, label: string, value: V) => React.JSX.Element;

export interface SelectProps<T extends FieldValues, U, V>
	extends ExtendedInputWrapperProps<T>,
		ClassNameProps {
	options: Array<SelectOption<U, V> | "divider">;
	control: Control<T>;
	name: Path<T>;
	defaultValue?: PathValue<T, Path<T>>;
	renderOption?: SelectRenderOption<U, V>;
	itemNotFoundLabel?: string | undefined;
	isNullable?: boolean;
	onRemove?: () => void;
	multiple?: boolean;
	max?: number;
	placeholder?: string;
	onChange?: (value: PathValue<T, Path<T>>) => void;
	middleware?: (value: SelectOption<U, V>["value"]) => void;
	multipleSelectionRender?: (items: Array<SelectOption<U, V>>) => React.JSX.Element;
	shouldUnregister?: boolean;
	"data-cy"?: string;
	withBackdrop?: boolean;
	/**
	 * Extra classes for the open options list. The list follows the button's width by default;
	 * a `min-w-*` here lets it outgrow a narrow button when the options carry more than a label.
	 */
	optionsClassName?: string;
}

export function Select<T extends FieldValues, U, V>({
	options,
	control,
	name,
	autoFocus,
	itemNotFoundLabel = localized("general.not_found"),
	isDisabled,
	isNullable: _isNullable = true,
	onRemove,
	multiple = false,
	max,
	className,
	placeholder,
	onChange,
	multipleSelectionRender,
	middleware,
	shouldUnregister,
	withBackdrop = false,
	optionsClassName,
	...props
}: SelectProps<T, U, V>) {
	/** Hooks */
	const { t } = useTranslation();

	const renderedOptions = options.map((option) =>
		option === "divider" ? (
			<Divider key={generateId()} />
		) : (
			<ListboxOption
				key={typeof option.value === "string" ? option.value : JSON.stringify(option.value)}
				value={option.value}
				disabled={option.isDisabled}
				className={clsx(
					"group listbox-option",
					`listbox-option-${props["data-cy"]}`,
					"data-disabled:opacity-40 data-disabled:cursor-not-allowed",
				)}
				data-cy={option.cyId || `${props["data-cy"]}-${option.value}`}
			>
				{props.renderOption ? (
					props.renderOption(option.item as U, option.label, option.value)
				) : (
					<p>{option.label}</p>
				)}
				<div className="listbox-option-icon">
					<Icon name="Check" />
				</div>
			</ListboxOption>
		),
	);

	const isNullable = isDisabled ? false : multiple ? false : _isNullable;
	const isSingleValueArray = multiple && max === 1;

	/** Render */
	return (
		<Controller
			control={control}
			name={name}
			defaultValue={props.defaultValue}
			shouldUnregister={shouldUnregister}
			render={({ field, formState }) => {
				return (
					<InputWrapper
						{...props}
						name={name}
						errors={formState.errors}
					>
						<Listbox
							value={
								(isSingleValueArray
									? (guardArray(field.value)[0] ?? null)
									: field.value) as PathValue<T, Path<T>>
							}
							onChange={(value) => {
								middleware?.(value);
								if (isSingleValueArray) {
									const nextValue = value == null ? [] : [value];
									field.onChange(nextValue);
									onChange?.(nextValue as PathValue<T, Path<T>>);
									return;
								}
								if (multiple) {
									let valueAsArray = guardArray(value).reverse();
									if (max) {
										valueAsArray = valueAsArray.slice(0, max);
									}
									field.onChange(valueAsArray);
								} else {
									field.onChange(value);
								}
								onChange?.(value);
							}}
							disabled={isDisabled}
							multiple={multiple && !isSingleValueArray}
						>
							{({ open }) => (
								<React.Fragment>
									{withBackdrop && open && (
										// `ListboxOptions` is `modal` by default, so Headless UI already locks
										// scrolling, inerts the rest of the tree and closes on outside click —
										// this only dims. Portaled to `document.body` so no ancestor stacking
										// context can trap a `fixed` layer: at `z-10` it lands after the button
										// and dims it too, while the options' own `z-505` keeps the panel lit.
										<Portal>
											<Overlay className="opacity-20! dark:opacity-70!" />
										</Portal>
									)}
									<RemoveWrapper
										hasValue={!!field.value}
										isNullable={isNullable}
										handleOnRemove={() => {
											onRemove?.();
											if (multiple) {
												field.onChange([]);
											} else {
												field.onChange(null);
											}
										}}
									>
										<ListboxButton
											id={field.name}
											// `items-center` because the button is a grid: without it the chevron,
											// smaller than the label's line box, hangs from the row's top edge.
											className={clsx(
												"cursor-pointer input",
												"z-10 relative gap-x-4 items-center",
												open ? "z-506" : "z-10",
												className,
											)}
											autoFocus={autoFocus}
											data-cy={props["data-cy"] || "select"}
											aria-required={props.isRequired}
											style={{
												display: "grid",
												gridTemplateColumns: "1fr auto",
											}}
										>
											<div className={cn("overflow-hidden min-w-0", "h-stack")}>
												{multiple ? (
													multipleSelectionRender ? (
														Array.isArray(field.value) && field.value.length > 0 ? (
															multipleSelectionRender(
																options.filter((option) => {
																	if (option === "divider") return false;
																	return isInArray(field.value, option.value);
																}) as SelectOption<U, V>[],
															)
														) : (
															<p className="truncate">{t("general.select")}</p>
														)
													) : (
														<p className="truncate">
															{Array.isArray(field.value) ? field.value.length : 0}{" "}
															{t("general.selected")}
														</p>
													)
												) : (
													<React.Fragment>
														{props.renderOption ? (
															(options
																.filter((option) => option !== "divider")
																.find((option) => option.value === field.value)?.item as U) ? (
																props.renderOption(
																	options
																		.filter((option) => option !== "divider")
																		.find((option) => option.value === field.value)?.item as U,
																	options
																		.filter((option) => option !== "divider")
																		.find((option) => option.value === field.value)?.label || "",
																	field.value,
																)
															) : (
																<p className="truncate">
																	{field.value
																		? options
																				.filter((option) => option !== "divider")
																				.find((option) => option.value === field.value)?.label ||
																			itemNotFoundLabel ||
																			""
																		: t("general.select")}
																</p>
															)
														) : (
															<p className="truncate">
																{/* An empty select shows its own placeholder before the generic
																    prompt — that is what the prop is for. */}
																{field.value
																	? options
																			.filter((option) => option !== "divider")
																			.find((option) => option.value === field.value)?.label ||
																		placeholder
																	: placeholder || t("general.select")}
															</p>
														)}
													</React.Fragment>
												)}
											</div>
											<Icon
												name="ChevronDown"
												aria-hidden="true"
											/>
										</ListboxButton>
									</RemoveWrapper>
									<ListboxOptions
										anchor={{ gap: 5, to: "bottom" }}
										className={cn(
											"w-[calc(var(--button-width))] listbox-options max-h-[250px]!",
											optionsClassName,
										)}
									>
										{renderedOptions}
									</ListboxOptions>
								</React.Fragment>
							)}
						</Listbox>
					</InputWrapper>
				);
			}}
		/>
	);
}
